// PDM-2770: the medication box of the hospitalization list holds two independent lines of up to 3 slots - ATC
// and active ingredient. Values are ORed within a slot and the non-empty slots are ANDed by the backend, which
// accepts any subset.
export const ATC_SLOT_NAMES = ['atcHierarchyIds1', 'atcHierarchyIds2', 'atcHierarchyIds3'] as const;
export const ACTIVE_INGREDIENT_SLOT_NAMES = ['activeIngredientIds1', 'activeIngredientIds2', 'activeIngredientIds3'] as const;
export const ATC_SLOT_LABELS = ['filter.patient.atc1', 'filter.patient.atc2', 'filter.patient.atc3'] as const;
export const ACTIVE_INGREDIENT_SLOT_LABELS = [
  'filter.patient.activeIngredient1',
  'filter.patient.activeIngredient2',
  'filter.patient.activeIngredient3',
] as const;
export type MedicationSlotName = typeof ATC_SLOT_NAMES[number] | typeof ACTIVE_INGREDIENT_SLOT_NAMES[number];
/** The slice of the hospitalization list filters these helpers read. Structural, so the caller just passes its own. */
export type MedicationFilterValues = Partial<Record<MedicationSlotName, string[]>>;

function isFilled(value: string[] | undefined): value is string[] {
  return value !== undefined && value.length > 0;
}

/** Number of medication conditions set across both lines; "on the same day" needs at least two of them to compare. */
export function countMedicationConditions(patients: MedicationFilterValues): number {
  return [...ATC_SLOT_NAMES, ...ACTIVE_INGREDIENT_SLOT_NAMES].filter((name) => isFilled(patients[name])).length;
}

/**
 * Slots leave no gaps: within each line everything still filled in slides up to the first free position, so
 * clearing a slot moves the ones below it up instead of leaving an empty slot behind. The two lines are packed
 * independently - an empty ATC slot says nothing about the active ingredients. Applied after every edit of the
 * box, the same way the biochemistry rows are packed.
 */
export function packMedicationSlots(patients: MedicationFilterValues): MedicationFilterValues {
  const packed: MedicationFilterValues = {};
  [ATC_SLOT_NAMES, ACTIVE_INGREDIENT_SLOT_NAMES].forEach((line) => {
    const kept = line.map((name) => patients[name]).filter(isFilled);
    line.forEach((name, index) => {
      packed[name] = kept[index];
    });
  });
  return packed;
}
